import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, Rocket, ShieldCheck, Star, Users, Clock } from "lucide-react";

export default function PlayStoreLaunchSection({ downloadLink }) {
  const scrollToRegister = () => {
    document.getElementById("lead-capture")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="playstore-launch-section" className="py-12 md:py-20 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2315803d' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="relative container mx-auto px-4 md:px-6 max-w-5xl">
        {/* Announcement badge */}
        <div className="flex justify-center mb-5">
          <span
            data-testid="playstore-badge"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-100 to-blue-50 border border-green-200 text-green-800 text-sm font-bold shadow-sm"
          >
            <Rocket className="w-4 h-4 text-green-600" />
            NOW LIVE ON GOOGLE PLAY
          </span>
        </div>

        <h2
          data-testid="playstore-headline"
          className="text-3xl md:text-4xl font-bold text-slate-900 text-center leading-tight"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Google Play Store ରେ <span className="text-green-700">Published ହୋଇଗଲା!</span>
        </h2>
        <p className="mt-3 text-center text-slate-600 text-sm md:text-base max-w-2xl mx-auto">
          Teacher Utility Toolkit ଏବେ Google Play Store ରେ Live — ସିଧା Download କରନ୍ତୁ!
          <strong className="text-slate-800"> Premium Access ମାତ୍ର ₹199/year!</strong>
        </p>

        {/* Google Play mockup */}
        <div className="mt-8 flex justify-center">
          <Card className="max-w-sm w-full border-2 border-slate-200 shadow-xl overflow-hidden">
            <CardContent className="p-0">
              {/* Play Store header */}
              <div className="bg-white px-4 py-4 flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-md flex-shrink-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_odisha-teachers/artifacts/ynn2abrl_Untitled%20%28512%20x%20512%20px%29%20%281%29.png"
                    alt="Teacher Utility Toolkit Logo"
                    className="w-full h-full object-cover"
                    data-testid="playstore-app-logo"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 text-base truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Teacher Utility Toolkit
                  </p>
                  <p className="text-xs text-green-600 font-medium">Education & Tools</p>
                  <div className="flex items-center gap-1 mt-1">
                    {[1,2,3,4,5].map((s) => (
                      <Star key={s} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="text-xs text-slate-500 ml-1">5.0</span>
                  </div>
                </div>
              </div>

              {/* Play Store style info bar */}
              <div className="grid grid-cols-3 border-t border-b border-slate-100 bg-slate-50">
                <div className="py-3 text-center border-r border-slate-100">
                  <p className="text-sm font-bold text-slate-900">1K+</p>
                  <p className="text-[10px] text-slate-500">Downloads</p>
                </div>
                <div className="py-3 text-center border-r border-slate-100">
                  <p className="text-sm font-bold text-slate-900">15 MB</p>
                  <p className="text-[10px] text-slate-500">Size</p>
                </div>
                <div className="py-3 text-center">
                  <p className="text-sm font-bold text-slate-900">3+</p>
                  <p className="text-[10px] text-slate-500">Age Rating</p>
                </div>
              </div>

              {/* Google Play icon + Install button */}
              <div className="px-4 py-4 flex items-center gap-3">
                {/* Google Play SVG icon */}
                <svg data-testid="google-play-icon" className="w-8 h-8 flex-shrink-0" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" fill="#34A853"/>
                  <path d="M104.6 499l221.8-221.8 60.1 60.1L104.6 499z" fill="#EA4335"/>
                  <path d="M25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l279.4-255.9L47 0C34 6.8 25.3 19.2 25.3 35.3z" fill="#4285F4"/>
                  <path d="M325.3 234.3L104.6 13l221.8 221.3h-1.1z" fill="#FBBC04"/>
                </svg>
                <div className="flex-1">
                  <p className="text-xs text-slate-500">Available on</p>
                  <p className="text-sm font-bold text-slate-900">Google Play</p>
                </div>
                <span className="px-4 py-1.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                  LIVE NOW
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Psychological trust hooks */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          <div data-testid="playstore-hook-0" className="flex items-start gap-3 bg-green-50 rounded-xl p-4 border border-green-100">
            <ShieldCheck className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-slate-900">Google Verified App</p>
              <p className="text-xs text-slate-500 mt-0.5">Play Protect ଦ୍ୱାରା verified — 100% ସୁରକ୍ଷିତ, ଆପଣଙ୍କ data safe</p>
            </div>
          </div>
          <div data-testid="playstore-hook-1" className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
            <Rocket className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-slate-900">ଆହୁରି ନୂଆ Tools ଆସୁଛି</p>
              <p className="text-xs text-slate-500 mt-0.5">Transfer Order Tracker, GPF Calculator, Pension Calculator ଓ ଅନେକ</p>
            </div>
          </div>
          <div data-testid="playstore-hook-2" className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
            <Users className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-slate-900">Early Adopter Bonus</p>
              <p className="text-xs text-slate-500 mt-0.5">ଆଗରୁ Register କରିଥିବା ଶିକ୍ଷକଙ୍କୁ special discount ଓ priority access ମିଳିବ</p>
            </div>
          </div>
        </div>

        {/* Urgency CTA */}
        <div className="mt-8 text-center">
          <Card className="max-w-md mx-auto bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-orange-500" />
                <p className="text-sm font-bold text-slate-900">
                  Google Play ରେ Live — ଏବେ Download କରନ୍ତୁ!
                </p>
              </div>
              <p className="text-xs text-slate-600 mb-4">
                Play Store ରେ Premium Price <strong>₹199/year</strong>। ଏବେ Download କରି <strong className="text-green-700">ସବୁ tools unlock</strong> କରନ୍ତୁ!
              </p>
              <Button
                data-testid="playstore-register-btn"
                asChild
                className="w-full h-11 bg-green-700 hover:bg-green-800 text-white rounded-lg font-semibold shadow-md"
              >
                <a href="https://play.google.com/store/apps/details?id=com.wnapp.id1781430851106&hl=en_IN" target="_blank" rel="noopener noreferrer">
                  <Rocket className="mr-2 w-4 h-4" />
                  Google Play ରୁ Download କରନ୍ତୁ
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
